import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { inr, imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Building2 } from "lucide-react";

export default function HostProperties() {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/host/hotels");
      setHotels(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-[#F7F7F9] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-display font-bold">My properties</h1>
            <p className="text-[#717171] mt-1">Manage your listings, rooms and pricing</p>
          </div>
          <Link to="/host/properties/new">
            <Button data-testid="add-property-btn-2" className="rounded-xl bg-[#222] hover:bg-black text-white">
              <Plus className="w-4 h-4 mr-1" /> Add property
            </Button>
          </Link>
        </div>

        {loading ? (
          <p className="text-[#717171]">Loading...</p>
        ) : hotels.length === 0 ? (
          <div className="bg-white border border-[#EBEBEB] rounded-2xl p-12 text-center">
            <Building2 className="w-12 h-12 text-[#717171] mx-auto mb-3" />
            <p className="text-[#717171] mb-4">You haven't listed any property yet.</p>
            <Link to="/host/properties/new">
              <Button className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white">List your first property</Button>
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hotels.map((h) => (
              <Link key={h.id} to={`/host/properties/${h.id}`} data-testid={`property-card-${h.id}`} className="bg-white border border-[#EBEBEB] rounded-2xl overflow-hidden hover-lift">
                <div className="relative">
                  <img src={h.images?.[0] ? imgUrl(h.images[0]) : "https://images.pexels.com/photos/237371/pexels-photo-237371.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600"} alt={h.name} className="w-full aspect-[4/3] object-cover" />
                  <span data-testid={`property-status-${h.id}`} className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full ${h.is_blocked ? "bg-[#FDE8E8] text-[#C13515]" : h.status === "approved" ? "bg-[#E6F4EA] text-[#137333]" : h.status === "rejected" ? "bg-[#F0F0F4] text-[#717171]" : "bg-[#FEF3E0] text-[#B36B00]"}`}>
                    {h.is_blocked ? "Blocked" : h.status === "approved" ? "Live" : h.status === "rejected" ? "Rejected" : "Pending review"}
                  </span>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-display font-semibold">{h.name}</h3>
                      <p className="text-sm text-[#717171]">{h.city}, {h.country}</p>
                    </div>
                    <Pencil className="w-4 h-4 text-[#717171]" />
                  </div>
                  <p className="mt-3 text-sm">
                    {h.min_price ? (<><span className="font-display font-bold">{inr(h.min_price)}</span><span className="text-[#717171]"> / night</span></>) : <span className="text-[#717171]">Add rooms to set price</span>}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
